import { ApiError } from '../types';
import { AuthState } from '../auth/AuthContext';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:5000';

// ─── Token provider ───────────────────────────────────────────────────────────
// AuthProvider calls configureTokenProvider() on mount so this module never
// needs to import MSAL or know about the auth implementation.
let _getToken: () => Promise<string> = async () => {
  throw new Error('Token provider not configured. AuthProvider must mount first.');
};

let _authState: AuthState | null = null;

export function configureTokenProvider(fn: () => Promise<string>, authState?: AuthState) {
  _getToken = fn;
  _authState = authState || null;
}

/** Internal: lets sibling API modules reuse the configured token provider for non-JSON requests. */
export function getAuthToken(): Promise<string> {
  return _getToken();
}

// ─── HTTP client ──────────────────────────────────────────────────────────────
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await _getToken();

  const url = `${BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string> | undefined),
  };

  // Add dev user header when auth state is available.
  // In deployed environments, authorization should come from the access token
  // (issuer/audience/groups). This header is primarily for local dev bypass.
  if (_authState) {
    const authState = _authState as AuthState;
    const email = authState.email || 'unknown@dev';

    headers['X-Dev-User'] = JSON.stringify({
      userId: authState.email || '',
      name: authState.name,
      email: email,
      role: authState.roles[0] || 'BRG.Member',
    });
  }

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = await response.json() as { message?: string; title?: string };
      message = body.message ?? body.title ?? message;
    } catch {
      // ignore parse errors
    }
    throw new ApiError(message, response.status);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  // Handle empty responses (e.g., from Results.Ok() with no body)
  const contentLength = response.headers.get('content-length');
  if (contentLength === '0' || response.status === 200 && response.headers.get('content-type')?.includes('text/plain')) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as unknown as T;
  }

  return JSON.parse(text) as T;
}

export const apiClient = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'GET' });
  },
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' });
  },
};
